import React, { useEffect, useMemo, useState } from "react";
import { toLocalISODate } from "../../lib/dates";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { BookOpen, FileDown, Search, Lightbulb, RotateCcw } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import api, { formatError } from "../../lib/api";
import { downloadPdf } from "../../components/FileUpload";
import { policyHtml } from "../public/Policies";
import { useTips } from "../../components/Tips";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const sections = (body) => body.split(/\n(?=## )/).map((b) => { const [h, ...rest] = b.trim().split("\n"); return { title: h.replace(/^## /, ""), body: rest.join("\n"), id: slug(h) }; }).filter((s) => s.title);

// Maps a page key (from ?from=) to the guide section that best matches it.
const FROM_MAP = { portal: "dashboard", "portal-standing": "good-standing", "portal-dues": "dues-levies-payments", "portal-payments": "dues-levies-payments", "portal-events": "events-meetings", "portal-welfare": "welfare", "portal-directory": "directory", "portal-documents": "documents", "portal-elections": "elections", "portal-notifications": "notifications", "portal-profile": "profile-your-data", admin: "officer-console-basics", "admin-members": "members-vetting", "admin-reconciliation": "reconciliation-ledger", "admin-dues": "dues-levies", "admin-welfare": "welfare-review", "admin-events": "events-meetings", "admin-meetings": "events-meetings", "admin-content": "content-site-settings", "admin-communications": "communications", "admin-elections": "elections", "admin-finance": "finance-reports", "admin-roles": "roles-access", "admin-audit": "audit-log" };

function Guide({ g, q }) {
  const secs = useMemo(() => sections(g.body).filter((s) => !q || `${s.title} ${s.body}`.toLowerCase().includes(q.toLowerCase())), [g.body, q]);
  return (
    <div className="grid gap-6 lg:grid-cols-[220px_1fr]" data-testid={`guide-${g.slug}`}>
      <nav className="hidden lg:block"><ul className="sticky top-24 space-y-1 text-sm">
        {secs.map((s) => <li key={s.id}><a href={`#${g.slug}-${s.id}`} className="block rounded px-2 py-1 text-muted-foreground hover:bg-gold/10 hover:text-crimson">{s.title}</a></li>)}
      </ul></nav>
      <div className="space-y-6">
        {secs.map((s) => (
          <section key={s.id} id={`${g.slug}-${s.id}`} className="glass scroll-mt-24 rounded-2xl p-5" data-testid={`guide-section-${g.slug}-${s.id}`}>
            <h3 className="font-serif-display text-xl font-bold text-crimson">{s.title}</h3>
            <div className="prose mt-2 max-w-none text-sm text-foreground/85" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(policyHtml(s.body)) }} />
          </section>
        ))}
        {secs.length === 0 && <p className="text-sm text-muted-foreground">No sections match “{q}”.</p>}
      </div>
    </div>
  );
}

export default function Help() {
  const { data } = useApi("/help/guides");
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const from = params.get("from") || "";
  const defaultTab = from.startsWith("admin") && data?.some((g) => g.slug === "officer") ? "officer" : "member";
  const tips = useTips();
  useEffect(() => {
    if (!data || !from) return;
    const id = `${defaultTab}-${FROM_MAP[from] || ""}`;
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
  }, [data, from, defaultTab]);
  if (!data) return null;
  return (
    <div data-testid="help-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow text-gold">Help centre</p><h1 className="font-serif-display text-3xl font-bold text-crimson lg:text-4xl">Tip sheet</h1>
          <p className="mt-1 text-sm text-muted-foreground">Everything you need to use the member platform{data.length > 1 && ", plus the officer console"}.</p></div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative"><Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the guide…" className="w-56 pl-8" data-testid="help-search" /></div>
          {data.map((g) => <Button key={g.slug} variant="outline" className="gap-1 border-gold text-crimson" onClick={() => downloadPdf(`/help/guides/${g.slug}.pdf`, `${g.slug}-guide.pdf`)} data-testid={`help-pdf-${g.slug}`}><FileDown size={15} /> {g.slug === "member" ? "Member" : "Officer"} guide PDF</Button>)}
        </div>
      </div>
      {tips?.state && (
        <div className="mb-6 glass flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4" data-testid="help-tips-prefs">
          <div className="flex items-center gap-3 text-sm"><Lightbulb size={18} className="text-gold" /><Label htmlFor="tips-switch">Show in-app tips as I navigate</Label><Switch id="tips-switch" checked={tips.state.enabled} onCheckedChange={tips.setEnabled} data-testid="tips-toggle" /></div>
          <Button size="sm" variant="ghost" className="gap-1" onClick={() => tips.reset().then(() => toast.success("Tips will show again on each page"))} data-testid="tips-reset"><RotateCcw size={14} /> Reset tips ({tips.state.dismissed.length} dismissed)</Button>
        </div>
      )}
      {data.length === 1 ? <Guide g={data[0]} q={q} /> : (
        <Tabs defaultValue={defaultTab}>
          <TabsList className="mb-5"><TabsTrigger value="member" data-testid="help-tab-member"><BookOpen size={15} className="mr-1" /> Member guide</TabsTrigger><TabsTrigger value="officer" data-testid="help-tab-officer"><BookOpen size={15} className="mr-1" /> Officer & admin guide</TabsTrigger></TabsList>
          {data.map((g) => <TabsContent key={g.slug} value={g.slug}><Guide g={g} q={q} /></TabsContent>)}
        </Tabs>
      )}
    </div>
  );
}

function GuideEditor({ g, reload }) {
  const [f, setF] = useState({ title: g.title, body: g.body });
  useEffect(() => setF({ title: g.title, body: g.body }), [g]);
  const save = async () => { try { await api.put(`/admin/help/guides/${g.slug}`, f); toast.success("Guide published"); reload(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const reset = async () => { try { await api.post(`/admin/help/guides/${g.slug}/reset`); toast.success("Default guide restored"); reload(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  return (
    <div className="glass rounded-2xl p-5" data-testid={`guide-editor-${g.slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-80" data-testid={`guide-${g.slug}-title`} /></div>
        <span className="text-xs text-muted-foreground">{g.is_default ? "Using default text" : `Revised ${toLocalISODate(g.updated_at)} by ${g.updated_by}`}</span>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Start a line with <code>## </code> for a section, <code>- </code> for bullet points.</p>
      <Textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={16} className="mt-2 font-mono text-xs" data-testid={`guide-${g.slug}-body`} />
      <div className="mt-3 flex justify-end gap-2"><Button variant="ghost" onClick={reset} data-testid={`guide-${g.slug}-reset`}>Restore default</Button><Button onClick={save} className="bg-crimson hover:bg-crimson-hover" data-testid={`guide-${g.slug}-save`}>Publish</Button></div>
    </div>
  );
}

export function HelpGuidesTab() {
  const { data, reload } = useApi("/help/guides");
  if (!data) return null;
  return <div className="space-y-6" data-testid="help-guides-tab">{data.map((g) => <div key={g.slug}><h3 className="mb-2 font-serif-display text-xl font-bold text-crimson">{g.title}</h3><GuideEditor g={g} reload={reload} /></div>)}</div>;
}
